'use client';

import { faCircle } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { RadioGroup, RadioGroupItem } from '@pickleballinc/ui/components/ui/radio-group';
import { cn } from '@pickleballinc/ui/lib/utils';
import { useForm, FormProvider, Controller } from 'react-hook-form';

import { ActionButtons } from '@/components/molecules';
import { ModalWrapper } from '@/components/organisms';
import { MatchupCompletedEnum, MatchupCompletedEnumTypes, MatchupStatusEnum } from '@/entities/matchup/constants';
import { useTeamLeagueInfo } from '@/hooks/custom-hooks/useTeamLeagueInfo';
import { formatTeamLeagueLabels } from '@/utils/helpers';

interface EditMatchupStatusModalProps {
	teamOneTitle: string;
	teamTwoTitle: string;
	onClose: () => void;
	onConfirm: (status: number) => void;
	onUndo: () => void;
	isSubmitting: boolean;
	matchupStatus: string;
	matchupCompletedType: string;
}

interface MatchupStatusFormData {
	status: number;
}

export const EditMatchupStatusModal = ({
	teamOneTitle,
	teamTwoTitle,
	onClose,
	onConfirm,
	onUndo,
	isSubmitting,
	matchupStatus,
	matchupCompletedType
}: EditMatchupStatusModalProps) => {
	const teamLeagueInfo = useTeamLeagueInfo();
	const labels = formatTeamLeagueLabels(teamLeagueInfo);

	let defaultStatus =
		matchupCompletedType === MatchupCompletedEnum.AWAY_TEAM_WITHDRAW_MATCHUP_COMPLETION_TYPE
			? MatchupCompletedEnumTypes.AWAY_TEAM_WITHDRAW_MATCHUP_COMPLETION_TYPE
			: matchupCompletedType === MatchupCompletedEnum.HOME_TEAM_WITHDRAW_MATCHUP_COMPLETION_TYPE
				? MatchupCompletedEnumTypes.HOME_TEAM_WITHDRAW_MATCHUP_COMPLETION_TYPE
				: matchupCompletedType === MatchupCompletedEnum.CANCELED_MATCHUP_COMPLETION_TYPE
					? MatchupCompletedEnumTypes.CANCELED_MATCHUP_COMPLETION_TYPE
					: -1;

	if (matchupStatus === MatchupStatusEnum.READY_TO_BE_STARTED_STATUS) {
		defaultStatus = -1;
	}

	const form = useForm<MatchupStatusFormData>({
		defaultValues: {
			status: defaultStatus
		}
	});

	const { handleSubmit, control, watch } = form;
	const currentStatus = watch('status');
	const hasStatusChanged = currentStatus !== defaultStatus;

	const handleFormSubmit = handleSubmit((data: MatchupStatusFormData) => {
		const status = parseInt(data.status.toString());
		if (status === -1) {
			onUndo();
		} else {
			onConfirm(status);
		}
	});

	return (
		<ModalWrapper title={`${labels.matchupSingularTitle} Status`} onClose={onClose} className="max-w-[450px]">
			<hr className="text-gray-400 my-4" />
			<FormProvider {...form}>
				<form onSubmit={handleFormSubmit} className="flex flex-col gap-4 px-2 sm:px-8">
					<Controller
						control={control}
						name="status"
						render={({ field }) => {
							const options = [
								{
									value: -1,
									label: 'None'
								},
								{
									value: MatchupCompletedEnumTypes.AWAY_TEAM_WITHDRAW_MATCHUP_COMPLETION_TYPE,
									label: `${teamTwoTitle} (A) withdraw`
								},
								{
									value: MatchupCompletedEnumTypes.HOME_TEAM_WITHDRAW_MATCHUP_COMPLETION_TYPE,
									label: `${teamOneTitle} (H) withdraw`
								},
								{
									value: MatchupCompletedEnumTypes.CANCELED_MATCHUP_COMPLETION_TYPE,
									label: `Cancel ${labels.matchupSingularTitle}`
								}
							];

							return (
								<RadioGroup
									className="flex flex-col gap-2"
									name="status"
									value={field.value.toString()}
									onValueChange={field.onChange}
								>
									{options.map(({ value, label }) => (
										<div key={value} className="flex gap-2 items-center bg-gray-100 rounded-lg px-3 py-2">
											<RadioGroupItem
												id={value.toString()}
												value={value.toString()}
												icon={({ className }: { className: string }) => (
													<FontAwesomeIcon icon={faCircle} className={cn('!size-2', className)} />
												)}
												className="size-5 cursor-pointer"
											/>
											<label htmlFor={value.toString()} className="text-gray-900 text-base cursor-pointer font-regular">
												{label}
											</label>
										</div>
									))}
								</RadioGroup>
							);
						}}
					/>

					<ActionButtons
						className="pt-10"
						onClose={onClose}
						submitButtonText="Save"
						closeButtonText="Close"
						submitButtonClassName="w-full"
						closeButtonClassName="w-full"
						submitButtonDisabled={isSubmitting || !hasStatusChanged}
						submitTooltipMessage={!hasStatusChanged ? 'Please select a different status to save changes' : undefined}
					/>
				</form>
			</FormProvider>
		</ModalWrapper>
	);
};
